import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { getProductDiscountAnalysisApi } from "../../../services/manage-product/productService";
import "./DiscountAnalysisDrawer.css";

const priorityClass = (value) => {
  const priority = String(value || "").toLowerCase();
  return `discount-analysis-priority discount-analysis-priority-${[
    "critical", "high", "medium", "low",
  ].includes(priority) ? priority : "medium"}`;
};

const formatLabel = (value) => String(value || "-")
  .toLowerCase()
  .split("_")
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(" ");

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isNaN(number)
    ? value
    : number.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const Section = ({ icon, title, children }) => (
  <section className="discount-analysis-card">
    <div className="discount-analysis-section-title">
      <i className={`fa ${icon} mr-2`} />{title}
    </div>
    {children}
  </section>
);

const EvidenceList = ({ items, icon, iconClass }) => (
  <div>
    {(items || []).map((item, index) => (
      <div className="discount-analysis-list-item" key={`${item}-${index}`}>
        <i className={`fa ${icon} ${iconClass} mr-2 mt-1`} />
        <div>{item}</div>
      </div>
    ))}
  </div>
);

const CandidateMetric = ({ label, value, suffix = "" }) => (
  <div className="discount-analysis-candidate-metric">
    <div className="text-muted small">{label}</div>
    <div className="font-weight-bold">{formatNumber(value)}{value !== null && value !== undefined ? suffix : ""}</div>
  </div>
);

const DiscountAnalysisDrawer = ({ productId, fallbackProductCode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setIsOpen(false);
    setAnalysis(null);
    setLoading(false);
    setError("");
  }, [productId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const loadAnalysis = () => {
    if (!productId || loading || analysis) return;
    setLoading(true);
    setError("");
    getProductDiscountAnalysisApi(productId)
      .then(({ response }) => {
        const nextAnalysis = response?.data?.analysis || response?.analysis || null;
        setAnalysis(nextAnalysis);
      })
      .catch((requestError) => {
        setError(
          requestError.response?.data?.message
          || requestError.message
          || "Failed to load the discount analysis."
        );
      })
      .finally(() => setLoading(false));
  };

  const openDrawer = () => {
    setIsOpen(true);
    loadAnalysis();
  };

  const drawer = isOpen ? ReactDOM.createPortal(
    <>
      <div className="discount-analysis-backdrop" onClick={() => setIsOpen(false)} />
      <aside className="discount-analysis-drawer" role="dialog" aria-modal="true" aria-label="AI discount analysis">
        <header className="discount-analysis-header">
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <div className="small mb-1" style={{ opacity: 0.8 }}>AI DISCOUNT ANALYSIS</div>
              <h4 className="mb-1 text-white">
                {analysis?.product_code || fallbackProductCode || "Product"}
                {analysis?.colour ? ` - ${analysis.colour}` : ""}
              </h4>
              {analysis?.confidence && (
                <span className="badge badge-light mt-1">{formatLabel(analysis.confidence)} confidence</span>
              )}
            </div>
            <button
              type="button"
              className="btn btn-link text-white p-0"
              onClick={() => setIsOpen(false)}
              aria-label="Close AI discount analysis"
              style={{ fontSize: "24px", lineHeight: 1 }}
            >
              &times;
            </button>
          </div>
        </header>

        <div className="discount-analysis-body">
          {loading && (
            <div className="text-center text-muted py-5">
              <i className="fa fa-spinner fa-spin fa-2x mb-3 d-block" />
              Analysing discount performance...
            </div>
          )}

          {!loading && error && (
            <div className="alert alert-danger">
              <div className="font-weight-bold mb-1">Analysis could not be loaded</div>
              <div className="mb-2">{error}</div>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={loadAnalysis}>Retry</button>
            </div>
          )}

          {!loading && !error && analysis && (
            <>
              <Section icon="fa-compass" title="Decision Summary">
                <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: "8px" }}>
                  <span className="badge badge-primary px-3 py-2">{formatLabel(analysis.decision)}</span>
                  <span className="badge badge-light px-3 py-2">
                    XGBoost: {formatNumber(analysis.xgboost_recommended_discount)}%
                  </span>
                  {analysis.llm_recommended_discount !== null && analysis.llm_recommended_discount !== undefined && (
                    <span className="badge badge-success px-3 py-2">
                      LLM recommendation: {formatNumber(analysis.llm_recommended_discount)}%
                    </span>
                  )}
                  {analysis.review_after_days !== null && analysis.review_after_days !== undefined && (
                    <span className="badge badge-warning px-3 py-2">Review in {analysis.review_after_days} days</span>
                  )}
                </div>
                <div className="font-weight-bold mb-1">Assessment</div>
                <p className="mb-3">{analysis.assessment || "-"}</p>
                <div className="font-weight-bold mb-1">Why this recommendation</div>
                <p className="mb-0">{analysis.recommendation_reason || "-"}</p>
              </Section>

              {analysis.xgboost_recommendation_evaluation && (
                <Section icon="fa-bar-chart" title="XGBoost Recommendation Evaluation">
                  <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: "8px" }}>
                    <span className="badge badge-light px-3 py-2">
                      XGBoost discount: {formatNumber(analysis.xgboost_recommended_discount)}%
                    </span>
                    <span className={`badge px-3 py-2 ${analysis.xgboost_recommendation_evaluation.is_reasonable ? "badge-success" : "badge-danger"}`}>
                      {analysis.xgboost_recommendation_evaluation.is_reasonable ? "Reasonable" : "Not reasonable for clearance"}
                    </span>
                    <span className="badge badge-secondary px-3 py-2">
                      {formatLabel(analysis.xgboost_recommendation_evaluation.status)}
                    </span>
                  </div>
                  <p className="mb-3">{analysis.xgboost_recommendation_evaluation.reason || "-"}</p>

                  {analysis.xgboost_recommendation_evaluation.supporting_evidence?.length > 0 && (
                    <div className="mb-3">
                      <div className="font-weight-bold mb-1 text-success">Supporting evidence</div>
                      <EvidenceList
                        items={analysis.xgboost_recommendation_evaluation.supporting_evidence}
                        icon="fa-check-circle"
                        iconClass="text-success"
                      />
                    </div>
                  )}

                  {analysis.xgboost_recommendation_evaluation.concerns?.length > 0 && (
                    <div>
                      <div className="font-weight-bold mb-1 text-danger">Concerns</div>
                      <EvidenceList
                        items={analysis.xgboost_recommendation_evaluation.concerns}
                        icon="fa-exclamation-circle"
                        iconClass="text-danger"
                      />
                    </div>
                  )}
                </Section>
              )}

              {analysis.llm_discount_recommendation && (
                <Section icon="fa-lightbulb-o" title="LLM Discount Recommendation">
                  <div className="discount-analysis-comparison mb-3">
                    <div>
                      <div className="small text-muted">XGBoost</div>
                      <div className="discount-analysis-comparison-value">
                        {formatNumber(analysis.xgboost_recommended_discount)}%
                      </div>
                    </div>
                    <div className="discount-analysis-comparison-arrow">
                      <i className="fa fa-long-arrow-right" />
                    </div>
                    <div>
                      <div className="small text-muted">LLM recommendation</div>
                      <div className="discount-analysis-comparison-value text-success">
                        {formatNumber(analysis.llm_recommended_discount)}%
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <strong>Selected candidate</strong>
                    <span className="badge badge-success px-3 py-2">
                      {formatLabel(analysis.llm_discount_recommendation.decision)}
                    </span>
                  </div>

                  {analysis.llm_discount_recommendation.selected_candidate && (
                    <div className="discount-analysis-candidate-grid mb-3">
                      <CandidateMetric
                        label="Discount"
                        value={analysis.llm_discount_recommendation.selected_candidate.discount}
                        suffix="%"
                      />
                      <CandidateMetric
                        label="Predicted Sales"
                        value={analysis.llm_discount_recommendation.selected_candidate.predicted_sales}
                      />
                      <CandidateMetric
                        label="Remaining Stock"
                        value={analysis.llm_discount_recommendation.selected_candidate.remaining_stock}
                      />
                      <CandidateMetric
                        label="Expected Profit"
                        value={analysis.llm_discount_recommendation.selected_candidate.expected_profit}
                      />
                    </div>
                  )}

                  <div className="discount-analysis-impact discount-analysis-impact-profit mb-2">
                    <strong>Profit impact</strong>
                    <div>{analysis.llm_discount_recommendation.expected_profit_impact || "-"}</div>
                  </div>
                  <div className="discount-analysis-impact discount-analysis-impact-clearance mb-2">
                    <strong>Clearance impact</strong>
                    <div>{analysis.llm_discount_recommendation.expected_clearance_impact || "-"}</div>
                  </div>
                  <div className="discount-analysis-impact discount-analysis-impact-risk mb-3">
                    <strong>Return-related risk</strong>
                    <div>{analysis.llm_discount_recommendation.return_related_risk || "-"}</div>
                  </div>

                  <div className="font-weight-bold mb-1">Why the LLM candidate is preferable</div>
                  <p className="mb-0">{analysis.llm_discount_recommendation.why_preferable || "-"}</p>
                </Section>
              )}

              {analysis.key_findings?.length > 0 && (
                <Section icon="fa-search" title="Key Findings">
                  {analysis.key_findings.map((finding, index) => (
                    <div className="discount-analysis-list-item" key={`${finding}-${index}`}>
                      <span className="discount-analysis-number">{index + 1}</span>
                      <div>{finding}</div>
                    </div>
                  ))}
                </Section>
              )}

              {analysis.recommended_actions?.length > 0 && (
                <Section icon="fa-check-square-o" title="Recommended Actions">
                  {analysis.recommended_actions.map((action, index) => (
                    <div className="discount-analysis-list-item" key={`${action.action}-${index}`}>
                      <span className="discount-analysis-number">{index + 1}</span>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <strong>{formatLabel(action.action)}</strong>
                          <span className={priorityClass(action.priority)}>{formatLabel(action.priority)}</span>
                        </div>
                        <div className="text-muted">{action.reason}</div>
                      </div>
                    </div>
                  ))}
                </Section>
              )}

              {analysis.risks?.length > 0 && (
                <Section icon="fa-exclamation-triangle" title="Risks">
                  {analysis.risks.map((risk, index) => (
                    <div className="discount-analysis-list-item" key={`${risk.type}-${index}`}>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <strong>{formatLabel(risk.type)}</strong>
                          <span className={priorityClass(risk.severity)}>{formatLabel(risk.severity)}</span>
                        </div>
                        <div className="text-muted">{risk.description}</div>
                      </div>
                    </div>
                  ))}
                </Section>
              )}

              {analysis.monitoring_metrics?.length > 0 && (
                <Section icon="fa-line-chart" title="Monitoring Plan">
                  {analysis.monitoring_metrics.map((metric, index) => (
                    <div className="discount-analysis-metric" key={`${metric.metric}-${index}`}>
                      <div className="font-weight-bold mb-1">{metric.metric}</div>
                      <div className="small text-muted">
                        Every {metric.period_days} days · Target {formatLabel(metric.comparison)} {formatNumber(metric.target)}
                      </div>
                    </div>
                  ))}
                </Section>
              )}

              {analysis.escalation_condition && (
                <Section icon="fa-level-up" title="Escalation Condition">
                  <div className="alert alert-warning mb-2">{analysis.escalation_condition.condition}</div>
                  <div className="font-weight-bold mb-1">Suggested action</div>
                  <div>{analysis.escalation_condition.suggested_action}</div>
                </Section>
              )}
            </>
          )}
        </div>
      </aside>
    </>,
    document.body
  ) : null;

  return (
    <>
      <button
        type="button"
        className="btn btn-sm ml-2 text-white"
        onClick={openDrawer}
        title="View AI discount analysis"
        style={{ backgroundColor: "#514a9d", borderRadius: "999px", padding: "4px 10px" }}
      >
        <i className="fa fa-lightbulb-o mr-1" />AI Analysis
      </button>
      {drawer}
    </>
  );
};

export default DiscountAnalysisDrawer;
